// client/src/components/leagues/LeagueLanding.js
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import '../matches/ScheduledMatches.css'
import './LeagueLanding.css'
import toastr from 'toastr'
import { findMatchBetweenPlayers } from '../../utils/match'
import ApiLeagues from '../../api/ApiLeagues'
import LeagueLandingHero from './LeagueLandingHero'
import HomeCourtSelector from './HomeCourtSelector'
import MatchCard from '../matches/MatchCard'
import ChatDrawer from '../chat/ChatDrawer'
import PlayoffsTab from './PlayoffsTab'
import { LEAGUE_STATUS } from '../../constants/leagueStatuses'
import OpponentCard from './OpponentCard'
import CancelMatchModal from '../common/CancelMatchModal'
import LeagueExplanationModal from './LeagueExplanationModal'
import ContactInfoModal from './ContactInfoModal'
import AvailabilityModal from '../scheduling/AvailabilityModal'
import useAuth from '../../hooks/useAuth'
import useContactInfoCheck from '../../hooks/useContactInfoCheck'
import { AVAILABILITY_INFO } from '../../constants/availabilityMessages.js'
import StaleDataBanner from '../common/StaleDataBanner'
import LeagueLandingAvailabilityModal from './LeagueLandingAvailabilityModal'
import useLeagueLandingData from './hooks/useLeagueLandingData'
import useLeagueLandingModals from './hooks/useLeagueLandingModals'
import useLeagueLandingChat from './hooks/useLeagueLandingChat'
import { getOpponentName, formatMatchDateTime } from './leagueLandingSelectors'

export default function LeagueLanding() {
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('division')

  const {
    division,
    setDivision,
    league,
    loading,
    setLoading,
    proposalsLoading,
    daysRemaining,
    primaryPlayer,
    setPrimaryPlayer,
    scheduledMatches,
    pendingCount,
    opponentGroups,
    unscheduledCount,
    hasScheduledMatches,
    hasPendingProposals,
    myHomeCourt,
  } = useLeagueLandingData({ leagueId, user })

  const contactInfo = useContactInfoCheck(user, primaryPlayer)

  const {
    cancelModalOpen,
    matchToCancel,
    isCanceling,
    staleDataWarning,
    showExplanationModal,
    showContactInfoModal,
    setShowContactInfoModal,
    availabilityModalOpen,
    setAvailabilityModalOpen,
    showLandingAvailabilityModal,
    landingAvailabilityModalVariant,
    editingHomeCourt,
    setEditingHomeCourt,
    pendingHomeCourt,
    setPendingHomeCourt,
    handleSchedulingAction,
    handleContactInfoComplete,
    handleCancelMatchClick,
    handleConfirmCancel,
    handleCloseModal,
    handleRetryRefresh,
    dismissStaleDataWarning,
    handleAddAvailability,
    handleAvailabilitySave,
    handleEnterAvailability,
    handleGoHome,
    handleDismissLandingModal,
    openExplanationModal,
    closeExplanationModal,
  } = useLeagueLandingModals({
    leagueId,
    navigate,
    division,
    setDivision,
    loading,
    setLoading,
    primaryPlayer,
    setPrimaryPlayer,
    contactInfo,
  })

  const {
    isChatOpen,
    focusedChannelId,
    chatFocusMode,
    handleMessageOpponent,
    handleOpenDivisionChat,
    handleCloseChat,
  } = useLeagueLandingChat({ division })

  if (loading)
    return (
      <PageLayout title="Loading...">
        <p>Loading league data...</p>
      </PageLayout>
    )

  if (!division) {
    return (
      <PageLayout title="League Home">
        <p>League details are not available yet. Please check back soon.</p>
      </PageLayout>
    )
  }

  const handleMatchClick = (match) => {
    navigate(`/leagues/${leagueId}/match/${match.id}/score`)
  }

  const isDoublesLeague = division?.league_type === 'Doubles'
  const currentPlayer = (division?.roster || []).find(
    (player) => Number(player.id) === Number(division?.player_id),
  )
  const currentTeamId = currentPlayer?.partner?.team_id || null

  return (
    <PageLayout title={division.league_name || league?.name}>
      {showLandingAvailabilityModal && landingAvailabilityModalVariant && (
        <LeagueLandingAvailabilityModal
          show={showLandingAvailabilityModal}
          variant={landingAvailabilityModalVariant}
          leagueId={Number(leagueId)}
          onEnterAvailability={handleEnterAvailability}
          onNavigateHome={handleGoHome}
          onDismiss={handleDismissLandingModal}
        />
      )}

      <LeagueExplanationModal
        isOpen={showExplanationModal}
        onClose={closeExplanationModal}
      />

      <ContactInfoModal
        show={showContactInfoModal}
        onComplete={handleContactInfoComplete}
        onDismiss={() => setShowContactInfoModal(false)}
        primaryPlayer={primaryPlayer}
        user={user}
      />

      <CancelMatchModal
        isOpen={cancelModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmCancel}
        isProcessing={isCanceling}
        matchDetails={
          matchToCancel
            ? {
                opponentName: getOpponentName(
                  matchToCancel,
                  division?.player_id,
                ),
                dateTime: formatMatchDateTime(matchToCancel.match_datetime),
              }
            : {}
        }
      />

      <AvailabilityModal
        isOpen={availabilityModalOpen}
        onClose={() => setAvailabilityModalOpen(false)}
        onSave={handleAvailabilitySave}
        mode="create"
        selectedDate={new Date()}
        leagueId={parseInt(leagueId, 10)}
        divisionId={division?.division_id}
        teamId={currentTeamId}
        isDoublesLeague={isDoublesLeague}
        leagueEndDate={league?.play_end}
      />

      <div className="container-fluid px-3">
        <div className="row justify-content-center mb-3">
          <div className="col-md-10 col-lg-8">
            <StaleDataBanner
              show={staleDataWarning}
              onRetry={handleRetryRefresh}
              onDismiss={dismissStaleDataWarning}
              className="stale-data-banner--league-landing"
            />
          </div>
        </div>

        <LeagueLandingHero
          leagueName={division.league_name}
          divisionName={division.division_name}
          daysRemaining={daysRemaining}
          leagueStartDate={league?.play_start}
          leagueEndDate={league?.play_end}
          unscheduledCount={unscheduledCount}
          homeCourtText={
            myHomeCourt
              ? [
                  myHomeCourt.name,
                  myHomeCourt.address_line1,
                  myHomeCourt.city,
                  myHomeCourt.state,
                  myHomeCourt.zip,
                ]
                  .filter(Boolean)
                  .join(', ')
              : 'Your home court is not set'
          }
          onEditHomeCourt={
            league?.league_status === LEAGUE_STATUS.REGISTRATION_OPEN
              ? () => {
                  setPendingHomeCourt(myHomeCourt || null)
                  setEditingHomeCourt(true)
                }
              : null
          }
          onSchedule={() => {
            handleSchedulingAction(() => {
              if (pendingCount > 0) {
                navigate(`/leagues/${leagueId}/proposals`)
              } else {
                navigate(`/leagues/${leagueId}/schedule`)
              }
            })
          }}
          onViewOpponentList={() => {
            navigate(`/leagues/${leagueId}/availability?tab=list`)
          }}
          onViewWeekly={() => {
            navigate(`/leagues/${leagueId}/availability`)
          }}
          onViewMonthly={() => {
            navigate(`/leagues/${leagueId}/schedule-month`)
          }}
          onStandings={() => navigate(`/leagues/${leagueId}/standings`)}
          onMatches={() => navigate(`/leagues/${leagueId}/matches`)}
          onShowExplanation={openExplanationModal}
          onAddAvailability={handleAddAvailability}
        />

        {!proposalsLoading && hasPendingProposals && (
          <div className="row justify-content-center mb-3">
            <div className="col-md-10 col-lg-8">
              <div className="ll-surface ll-accent-border">
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="mb-1 text-primary">
                        📬 Match Proposals Inbox
                      </h6>
                      <p className="mb-0 small text-muted">
                        You have {pendingCount} pending match proposal
                        {pendingCount !== 1 ? 's' : ''} to review
                      </p>
                    </div>
                    <button
                      className="ll-btn ll-btn-primary position-relative"
                      onClick={() => navigate(`/leagues/${leagueId}/proposals`)}
                      data-track="navigation.view_all_proposals"
                      title="Review pending proposals"
                      aria-label={`View Inbox - ${pendingCount} pending proposal${
                        pendingCount !== 1 ? 's' : ''
                      }`}
                    >
                      View Inbox
                      <span
                        className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark"
                        style={{ fontSize: '0.7em', minWidth: '1.5em' }}
                        aria-hidden="true"
                      >
                        {pendingCount}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row justify-content-center mb-3">
          <div className="col-md-10 col-lg-8">
            <div
              className="alert alert-info d-flex align-items-start"
              role="status"
            >
              <div className="flex-grow-1" style={{ fontSize: '0.9rem' }}>
                <strong className="d-block mb-1">
                  {AVAILABILITY_INFO.HEADING}
                </strong>
                <p className="mb-1">{AVAILABILITY_INFO.DESCRIPTION}</p>
                <p className="mb-0 text-primary">
                  {AVAILABILITY_INFO.ENCOURAGEMENT}
                </p>
                <button
                  type="button"
                  data-testid="availability-info-add-button"
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={handleAddAvailability}
                >
                  Add Availability
                </button>
              </div>
            </div>
          </div>
        </div>

        {(league?.league_status === LEAGUE_STATUS.PLAYOFFS ||
          league?.league_status === LEAGUE_STATUS.COMPLETED) && (
          <div className="ll-tab-container mb-4">
            <div className="ll-tab-nav">
              <button
                className={`ll-tab-button ${
                  activeTab === 'division' ? 'll-tab-active' : ''
                }`}
                onClick={() => setActiveTab('division')}
                type="button"
                role="tab"
                aria-selected={activeTab === 'division'}
                aria-controls="division-panel"
                id="division-tab"
              >
                <span className="ll-tab-icon">🎾</span>
                <div className="ll-tab-text">
                  <div className="ll-tab-title">Regular Season</div>
                  <div className="ll-tab-subtitle">Matches & Standings</div>
                </div>
              </button>
              <button
                className={`ll-tab-button ${
                  activeTab === 'playoffs' ? 'll-tab-active' : ''
                }`}
                onClick={() => setActiveTab('playoffs')}
                type="button"
                role="tab"
                aria-selected={activeTab === 'playoffs'}
                aria-controls="playoffs-panel"
                id="playoffs-tab"
              >
                <span className="ll-tab-icon">🏆</span>
                <div className="ll-tab-text">
                  <div className="ll-tab-title">Playoff Bracket</div>
                  <div className="ll-tab-subtitle">Tournament Results</div>
                </div>
              </button>
            </div>
          </div>
        )}

        {(league?.league_status === LEAGUE_STATUS.PLAYOFFS ||
          league?.league_status === LEAGUE_STATUS.COMPLETED) &&
        activeTab === 'playoffs' ? (
          <div
            role="tabpanel"
            id="playoffs-panel"
            aria-labelledby="playoffs-tab"
          >
            <PlayoffsTab
              leagueId={leagueId}
              league={league}
              leagueLevelId={division?.league_level_id ?? null}
            />
          </div>
        ) : (
          <div
            {...(league?.league_status === LEAGUE_STATUS.PLAYOFFS ||
            league?.league_status === LEAGUE_STATUS.COMPLETED
              ? {
                  role: 'tabpanel',
                  id: 'division-panel',
                  'aria-labelledby': 'division-tab',
                }
              : {})}
          >
            {editingHomeCourt && (
              <section className="ll-section-spacing">
                <div className="row">
                  <div className="col-12">
                    <div className="card card-body ll-card">
                      <HomeCourtSelector
                        value={pendingHomeCourt}
                        onChange={setPendingHomeCourt}
                      />
                      <div className="mt-2">
                        <button
                          className="ll-btn ll-btn-primary"
                          onClick={async () => {
                            if (!pendingHomeCourt?.id) return
                            try {
                              await ApiLeagues.setHomeCourt({
                                league_id: parseInt(leagueId, 10),
                                player_id: division.player_id,
                                court_id: pendingHomeCourt.id,
                              })
                              const updatedRoster = (division.roster || []).map(
                                (player) =>
                                  player.id === division.player_id
                                    ? {
                                        ...player,
                                        home_court: pendingHomeCourt,
                                      }
                                    : player,
                              )
                              setDivision({
                                ...division,
                                roster: updatedRoster,
                              })
                              setEditingHomeCourt(false)
                            } catch (error) {
                              toastr.error(
                                error?.message || 'Failed to update home court',
                              )
                            }
                          }}
                        >
                          Save
                        </button>
                        <button
                          className="ll-btn ll-btn-outline ms-2"
                          onClick={() => setEditingHomeCourt(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {hasScheduledMatches && (
              <div className="row justify-content-center mb-4">
                <div className="col-md-8 ll-matches">
                  <h5 className="mb-3">Scheduled Matches</h5>
                  <div className="matches-list">
                    {scheduledMatches.map((match) => (
                      <MatchCard
                        key={match.id}
                        match={{
                          ...match,
                          league_id: parseInt(leagueId, 10),
                        }}
                        onClickMatch={handleMatchClick}
                        onCancelMatch={handleCancelMatchClick}
                        currentPlayerId={division.player_id}
                        calendarEnabled={true}
                        leagueName={division.league_name}
                        divisionName={division.division_name}
                        showLeagueDivision={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm ll-card">
                  <div className="card-body">
                    <h5 className="mb-3">Division Opponents</h5>
                    <div className="opponent-cards-container">
                      {opponentGroups.map((group) => {
                        const primary = group[0]
                        const doublesPartner = group[1] || null
                        const matchWithOpponent = findMatchBetweenPlayers(
                          division.matches,
                          division.player_id,
                          primary.id,
                        )

                        return (
                          <OpponentCard
                            key={primary.id}
                            opponent={primary}
                            partner={doublesPartner}
                            onMessage={handleMessageOpponent}
                            leagueId={Number(leagueId)}
                            divisionId={division.division_id}
                            currentPlayerId={division.player_id}
                            match={matchWithOpponent}
                          />
                        )
                      })}
                    </div>
                    <div className="text-center mt-3">
                      <button
                        className="ll-btn ll-btn-outline-primary"
                        onClick={() =>
                          navigate(`/leagues/${leagueId}/standings`)
                        }
                      >
                        🏆 View Full League Standings
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <ChatDrawer
              isOpen={isChatOpen}
              onClose={handleCloseChat}
              onOpen={handleOpenDivisionChat}
              channelId={chatFocusMode ? focusedChannelId : null}
              hideSidebar={chatFocusMode}
              divisionId={
                !chatFocusMode || !focusedChannelId
                  ? division.division_id
                  : null
              }
              leagueId={
                !chatFocusMode || !focusedChannelId ? division.league_id : null
              }
            />
          </div>
        )}
      </div>
    </PageLayout>
  )
}
